import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core";
import { getClient } from "@State/index";
import ModalLayout from "@View/components/ModalLayout";
import ButtonSave from "@View/components/buttons/ButtonSave";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import DefaultSelect from "@View/components/formInputs/DefaultSelect";
import { ClientRolesForSelect } from "@Utils/types";
import { canAddAbility } from "@Security/stateHelpers";
import getAPI from "@API/index";

const useStyles = makeStyles(() => ({
  errorText: {
    fontSize: "12px",
    color: "rgba(164, 36, 28, 1)",
    marginBottom: "8px",
  },
}));

const status = [{ value: "Active" }, { value: "Disable" }];

const EditClientPage = ({ clientId, open, handleClose, onSaved }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const client = useSelector((state) => state.client.client);
  const groupsList = useSelector((state) => state.customer.groups);
  const groupsListValue = useMemo(
    () => groupsList.map((i) => ({ name: i.name, value: i.id })),
    [groupsList]
  );

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [globalError, setGlobalError] = useState("");

  useEffect(() => {
    dispatch(getClient(clientId));
  }, [dispatch, clientId]);

  useEffect(() => {
    setForm({ ...client });
  }, [client]);

  useEffect(() => {
    if (open) setGlobalError("");
  }, [open]);

  const setField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (saving) return;

    const payload = {
      id: form.id,
      name: form.name.trim(),
      externalId: form.externalId.trim(),
      type: form.type,
      email: form.email.trim(),
      status: form.status.trim(),
      apiKey: form.apiKey,
      customerId: form.customerId,
      customerGroupId: form.customerGroupId,
      role: form.role,
    };

    setSaving(true);
    setGlobalError("");
    getAPI()
      .request({
        method: "put",
        url: "/api/Client",
        data: payload,
        suppressError: false,
      })
      .then(() => {
        onSaved?.();
        handleClose();
      })      
      .finally(() => setSaving(false));
  };

  if (!form || !form.id) {
    return null;
  }

  const editable = canAddAbility();

  const footer = (
    <>
      {globalError && <div className={classes.errorText}>{globalError}</div>}
      {editable && (
        <ButtonSave marginTop="0" disabled={saving}>
          buttons.Save
        </ButtonSave>
      )}
    </>
  );

  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title="h1.Edit Client"
      onSubmit={handleSubmit}
      footer={footer}
    >      
      <DefaultInput
        value={form.name}
        label="inputs.CompanyName"
        disabled={!editable}
        onChange={setField("name")}
      />
      <DefaultInput
        value={form.externalId}
        label="inputs.External Id"
        disabled={!editable}
        onChange={setField("externalId")}
      />
      <DefaultInput
        value={form.email}
        label="inputs.Email"
        disabled={!editable}
        onChange={setField("email")}
      />
      <DefaultSelect
        value={form.role}
        label="inputs.role"
        dropdowns={ClientRolesForSelect}
        disabled={!editable}
        onChange={setField("role")}
      />
      <DefaultSelect
        value={form.customerGroupId}
        label="inputs.group"
        dropdowns={groupsListValue}
        disabled={!editable}
        onChange={setField("customerGroupId")}
      />
      <DefaultSelect
        value={`${form.status}`}
        label="inputs.Status"
        dropdowns={status}
        disabled={!editable}
        onChange={setField("status")}
      />
    </ModalLayout>
  );
};

export default EditClientPage;
